import { supabase } from '@/lib/supabase';
import { fetchAllRows } from '@/lib/fetchAllRows';
import { getMinorUnitsSync, convertMinorUnits } from '@sharecost/shared';
import { fetchExchangeRates } from '@/api/currencies';
import { distributeCrossCurrencyMinor } from '@/lib/splitDistribution';
import type { GroupWithMembers } from '@/api/groups';
import type { UserBalance } from '@/hooks/useBalances';

interface RawBalanceData {
  expenses: {
    id: string;
    group_id: string;
    paid_by: string;
    amount_minor_src: number;
    amount_minor_usd: number;
    currency_code_src: string;
    expense_date: string;
  }[];
  splits: {
    expense_id: string;
    group_id: string;
    user_id: string;
    amount_minor: number;
    amount_minor_usd: number;
  }[];
  settlements: {
    group_id: string;
    from_user_id: string;
    to_user_id: string;
    amount_minor: number;
    currency_code: string;
  }[];
  rates: Map<string, number>;
}

/**
 * Batch-fetch balance data for all groups at once (4 queries total).
 */
export async function fetchAllGroupsBalanceData(
  groupIds: string[]
): Promise<RawBalanceData> {
  const [expenses, splits, settlements, rates] = await Promise.all([
    fetchAllRows(
      supabase
        .from('expenses')
        .select('id, group_id, paid_by, amount_minor_src, amount_minor_usd, currency_code_src, expense_date')
        .in('group_id', groupIds)
        .eq('_deleted', false),
    ),
    fetchAllRows(
      supabase
        .from('expense_splits')
        .select('expense_id, group_id, user_id, amount_minor, amount_minor_usd')
        .in('group_id', groupIds)
        .eq('_deleted', false),
    ),
    fetchAllRows(
      supabase
        .from('settlements')
        .select('group_id, from_user_id, to_user_id, amount_minor, currency_code')
        .in('group_id', groupIds)
        .eq('_deleted', false),
    ),
    fetchExchangeRates(),
  ]);

  return { expenses, splits, settlements, rates };
}

/**
 * Calculate current user's balance for each group from batch data.
 * Uses the same dual-tracking algorithm as useBalances.ts.
 */
export function calculateUserBalanceForGroups(
  userId: string,
  groups: GroupWithMembers[],
  data: RawBalanceData
): Map<string, UserBalance> {
  // Index raw data by group_id
  const expensesByGroup = new Map<string, RawBalanceData['expenses']>();
  for (const e of data.expenses) {
    const arr = expensesByGroup.get(e.group_id) ?? [];
    arr.push(e);
    expensesByGroup.set(e.group_id, arr);
  }

  const splitsByGroup = new Map<string, RawBalanceData['splits']>();
  for (const s of data.splits) {
    const arr = splitsByGroup.get(s.group_id) ?? [];
    arr.push(s);
    splitsByGroup.set(s.group_id, arr);
  }

  const settlementsByGroup = new Map<string, RawBalanceData['settlements']>();
  for (const s of data.settlements) {
    const arr = settlementsByGroup.get(s.group_id) ?? [];
    arr.push(s);
    settlementsByGroup.set(s.group_id, arr);
  }

  const result = new Map<string, UserBalance>();

  for (const group of groups) {
    const groupCurrency = group.currency_code;
    const groupMinorUnits = getMinorUnitsSync(groupCurrency);
    const expenses = expensesByGroup.get(group.id) ?? [];
    const allSplits = splitsByGroup.get(group.id) ?? [];
    const settlements = settlementsByGroup.get(group.id) ?? [];

    // Group splits by expense_id
    const splitsByExpense = new Map<string, typeof allSplits>();
    for (const s of allSplits) {
      const arr = splitsByExpense.get(s.expense_id) ?? [];
      arr.push(s);
      splitsByExpense.set(s.expense_id, arr);
    }

    // Balance accumulator — we only track the current user
    let balance = 0;

    // Process expenses (dual-tracking)
    for (const expense of expenses) {
      const splits = splitsByExpense.get(expense.id) ?? [];
      // Parity with mobile: skip expenses with no split rows (unattributable /
      // splits not yet synced). Mobile excludes them via its validExpenseIds
      // guard, so counting them here would diverge between platforms.
      if (splits.length === 0) continue;
      const isSameCurrency = expense.currency_code_src === groupCurrency;

      if (isSameCurrency) {
        if (expense.paid_by === userId) {
          balance += Number(expense.amount_minor_src);
        }
        for (const split of splits) {
          if (split.user_id === userId) {
            balance -= Number(split.amount_minor);
          }
        }
      } else {
        // Cross-currency — convert via USD
        const groupRate = data.rates.get(groupCurrency) ?? 1;
        const usdToGroup = groupRate > 0 ? 1 / groupRate : 1;

        const expenseInGroup = convertMinorUnits(
          Number(expense.amount_minor_usd), usdToGroup, 2, groupMinorUnits
        );

        if (expense.paid_by === userId) {
          balance += expenseInGroup;
        }

        // Proportional split distribution — shared with the mobile balance
        // engine (see lib/splitDistribution.ts) so the dashboard balance matches.
        const splitShares = distributeCrossCurrencyMinor(
          splits.map((s) => Number(s.amount_minor)),
          expenseInGroup
        );
        for (let i = 0; i < splits.length; i++) {
          if (splits[i].user_id === userId) {
            balance -= splitShares[i];
          }
        }
      }
    }

    // Process settlements
    for (const settlement of settlements) {
      const isRelevant =
        settlement.from_user_id === userId || settlement.to_user_id === userId;
      if (!isRelevant) continue;

      const isSameCurrency = settlement.currency_code === groupCurrency;
      let amount: number;

      if (isSameCurrency) {
        amount = Number(settlement.amount_minor);
      } else {
        const settlementRate = data.rates.get(settlement.currency_code) ?? 1;
        const groupRate = data.rates.get(groupCurrency) ?? 1;
        const settlementMinorUnits = getMinorUnitsSync(settlement.currency_code);
        const crossRate = groupRate > 0 ? settlementRate / groupRate : 1;
        amount = convertMinorUnits(
          Number(settlement.amount_minor), crossRate, settlementMinorUnits, groupMinorUnits
        );
      }

      if (settlement.from_user_id === userId) {
        balance += amount; // Debtor paid
      }
      if (settlement.to_user_id === userId) {
        balance -= amount; // Creditor received
      }
    }

    result.set(group.id, {
      userId,
      balanceMinor: balance,
      currency: groupCurrency,
    });
  }

  return result;
}

export interface GroupStats {
  expenseCount: number;
  totalSpentMinor: number;
  lastExpenseDate: string | null;
  weeklyActivity: number[];
}

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;
const SPARKLINE_WEEKS = 8;

/**
 * Derive per-group stats from the same batch data used for balance calculation.
 * Zero additional network calls.
 */
export function calculateGroupStats(
  groups: GroupWithMembers[],
  data: RawBalanceData
): Map<string, GroupStats> {
  const now = Date.now();
  const result = new Map<string, GroupStats>();

  // Index expenses by group
  const byGroup = new Map<string, RawBalanceData['expenses']>();
  for (const e of data.expenses) {
    const arr = byGroup.get(e.group_id) ?? [];
    arr.push(e);
    byGroup.set(e.group_id, arr);
  }

  for (const group of groups) {
    const expenses = byGroup.get(group.id) ?? [];
    const groupMinorUnits = getMinorUnitsSync(group.currency_code);
    const groupRate = data.rates.get(group.currency_code) ?? 1;
    const usdToGroup = groupRate > 0 ? 1 / groupRate : 1;
    const weekly = new Array<number>(SPARKLINE_WEEKS).fill(0);
    let lastDate: string | null = null;
    let nativeMinor = 0; // same-currency expenses, in group minor units
    let crossUsdMinor = 0; // cross-currency expenses, accumulated in USD minor

    for (const e of expenses) {
      // Parity with mobile (calculateGroupBalancesForHome): include cross-currency
      // expenses converted via USD instead of dropping them. Same-currency uses the
      // raw source amount; cross-currency is summed in USD and converted once below.
      if (e.currency_code_src === group.currency_code) {
        nativeMinor += Number(e.amount_minor_src);
      } else {
        crossUsdMinor += Number(e.amount_minor_usd);
      }

      // Track most recent expense
      if (!lastDate || e.expense_date > lastDate) {
        lastDate = e.expense_date;
      }

      // Bucket into weekly sparkline bins
      const age = now - new Date(e.expense_date).getTime();
      const weekIndex = Math.floor(age / WEEK_MS);
      if (weekIndex >= 0 && weekIndex < SPARKLINE_WEEKS) {
        // Index 0 = oldest week, so reverse: slot = WEEKS-1-weekIndex
        weekly[SPARKLINE_WEEKS - 1 - weekIndex]++;
      }
    }

    const totalMinor =
      nativeMinor +
      (crossUsdMinor > 0
        ? convertMinorUnits(crossUsdMinor, usdToGroup, 2, groupMinorUnits)
        : 0);

    result.set(group.id, {
      expenseCount: expenses.length,
      totalSpentMinor: totalMinor,
      lastExpenseDate: lastDate,
      weeklyActivity: weekly,
    });
  }

  return result;
}
